import { toShortDateTime } from "@/shared/utils/date";
import { useGetMeSuspense } from "@/spz-api/user/user";
import { ActivityItem } from "@spz/ui/components/activity-item";
import { Separator } from "@spz/ui/components/separator";
import { Skeleton } from "@spz/ui/components/skeleton";
import { Typography } from "@spz/ui/components/typography";
import React from "react";
import { useTranslation } from "react-i18next";
import { useResultDetailActivityListingViewModel } from "../../view-models/result-detail-activity-listing.view-model";

type ResultDetailActivityListingProps = {
  controlId: string;
  controlResultId: string;
  canWriteCommentsAndAttachments: boolean;
};

export const ResultDetailActivityListing = ({
  controlId,
  controlResultId,
  canWriteCommentsAndAttachments,
}: ResultDetailActivityListingProps) => {
  const { t, i18n } = useTranslation();
  const { data: me } = useGetMeSuspense();

  const {
    activityList,
    isLoading,
    editingCommentId,
    onDelete,
    onCancel,
    onEdit,
    onValidate,
    updateContent,
    handleAttachmentClick,
  } = useResultDetailActivityListingViewModel({
    controlId,
    controlResultId,
    userId: me.id,
  });

  return (
    <div className="space-y-4">
      {!canWriteCommentsAndAttachments && (
        <Typography className="text-gray-500 font-base font-normal text-sm">
          {t("add_activity_not_allowed")}
        </Typography>
      )}

      {isLoading ? (
        <div className="space-y-6">
          {/* Skeleton loader for activity items */}
          {[1, 2, 3].map((index) => (
            <div key={`skeleton-${index}`} className="space-y-3">
              <Separator orientation="horizontal" className="w-full" />
              <div className="space-y-3">
                {/* Header skeleton */}
                <div className="flex items-center justify-between">
                  {/* Name and date skeleton */}
                  <div className="flex flex-col space-y-1">
                    <Skeleton className="h-3 w-[150px]" />
                    <Skeleton className="h-3 w-[100px]" />
                  </div>
                  {/* Action buttons skeleton */}
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-6 w-6 rounded" />
                    <Skeleton className="h-6 w-6 rounded" />
                  </div>
                </div>
                {/* Content skeleton */}
                <div className="space-y-2">
                  <Skeleton className="h-3 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : activityList?.length === 0 ? (
        <>
          <Separator orientation="horizontal" />
          <Typography className="text-gray-500 my-3">
            {t("no_activity_recorded_for_this_result")}
          </Typography>
        </>
      ) : (
        activityList?.map((item) => (
          <React.Fragment key={item.id}>
            <Separator
              key={`separator-${item.id}`}
              orientation="horizontal"
              className="w-full"
            />
            <ActivityItem.Root
              isEditing={editingCommentId === item.comment?.id}
            >
              {/* Header with author info and action buttons */}
              <ActivityItem.Header>
                <ActivityItem.Header.Info
                  author={item.actor.fullName}
                  date={toShortDateTime(item.createdAt, i18n.language)}
                  updateDate={
                    item.comment?.hasBeenUpdated && item.comment.updatedAt
                      ? toShortDateTime(item.comment.updatedAt, i18n.language)
                      : undefined
                  }
                />
                <ActivityItem.Header.Actions>
                  <ActivityItem.Header.ViewActions>
                    {item.comment && item.canDelete(me.id) && (
                      <ActivityItem.Header.DeleteButton
                        commentId={item.comment.id}
                        attachmentOnly={false}
                        onClick={(_id, _attachmentOnly, anchor) =>
                          onDelete(item, anchor)
                        }
                      />
                    )}
                    {item.canEdit(me.id) && (
                      <ActivityItem.Header.EditButton
                        onClick={() => onEdit(item)}
                      />
                    )}
                  </ActivityItem.Header.ViewActions>
                  <ActivityItem.Header.EditActions>
                    <ActivityItem.Header.CancelButton onClick={onCancel} />
                    <ActivityItem.Header.ValidateButton
                      onClick={() => onValidate(item)}
                    />
                  </ActivityItem.Header.EditActions>
                </ActivityItem.Header.Actions>
              </ActivityItem.Header>

              {/* Comment section - only render if exists */}
              {item.comment && (
                <ActivityItem.Comment
                  key={item.commentKey}
                  id={item.id}
                  content={item.comment.content}
                  onContentChange={(content) =>
                    // biome-ignore lint/suspicious/noExplicitAny: RichTextDto type comes from API package
                    updateContent(content as any)
                  }
                />
              )}

              {/* Attachments section - only render if exists */}
              {item.attachment && (
                <ActivityItem.Attachments
                  attachments={[item.attachment]}
                  onAttachmentClick={handleAttachmentClick}
                />
              )}

              {/* Workflow step change section */}
              {item.workflowStepChange && (
                <ActivityItem.WorkflowStepChange
                  from={item.workflowStepChange.from}
                  to={item.workflowStepChange.to}
                />
              )}

              {/* Workflow conclusion change section */}
              {item.workflowConclusionChange?.to && (
                <ActivityItem.WorkflowConclusionChange
                  to={item.workflowConclusionChange.to}
                />
              )}

              {/* Owner change section */}
              {item.ownerChange && (
                <ActivityItem.OwnerChange
                  from={
                    item.ownerChange.from
                      ? {
                          firstName: item.ownerChange.from.firstName,
                          lastName: item.ownerChange.from.lastName,
                          avatarUrl: item.ownerChange.from.profilePictureUrl,
                        }
                      : null
                  }
                  to={
                    item.ownerChange.to
                      ? {
                          firstName: item.ownerChange.to.firstName,
                          lastName: item.ownerChange.to.lastName,
                          avatarUrl: item.ownerChange.to.profilePictureUrl,
                        }
                      : null
                  }
                />
              )}
              {/* Priority change section */}
              {item.priorityChange && (
                <ActivityItem.PriorityChange
                  from={item.priorityChange.from}
                  to={item.priorityChange.to}
                />
              )}
            </ActivityItem.Root>
          </React.Fragment>
        ))
      )}
    </div>
  );
};
